import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Building2, Lightbulb, Globe, Network, BarChart3, ChevronLeft, ChevronRight, MapPin, Activity, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import NewsletterForm from '@/components/NewsletterForm';
import { fetchContent } from '@/lib/contentApi';

const jsonLdSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Ogaba Joseph Attah",
  "url": "https://www.ogabajosephattah.com",
  "jobTitle": "Technology Founder & Digital Infrastructure Intelligence Leader",
  "description": "Ogaba Joseph Attah is a technology entrepreneur and founder specialising in digital infrastructure intelligence, scalable connectivity systems, and decision-ready data platforms.",
  "sameAs": [
    "https://www.linkedin.com/in/ogaba-joseph-attah-6a7851118"
  ],
  "knowsAbout": [
    "Digital Infrastructure Intelligence",
    "FTTH Networks",
    "Fibre Optic Systems",
    "Infrastructure Analytics",
    "Decision Intelligence",
    "Emerging Market Technology"
  ],
  "founder": [
    {
      "@type": "Organization",
      "name": "Nexintell Limited",
      "url": "https://www.nexintell.co"
    },
    {
      "@type": "Organization",
      "name": "Infralytics-GIIS",
      "url": "https://www.infralytics.co"
    },
    {
      "@type": "Organization",
      "name": "Evolve Digital Africa",
      "url": "https://www.evolvedigitalafrica.com.ng"
    }
  ]
};

const focusAreas = [
  { icon: Network, title: 'Digital & Infrastructure Intelligence', desc: 'Transforming fragmented data into decision-ready insight' },
  { icon: Globe, title: 'Scalable Connectivity Systems', desc: 'FTTH, Fibre, and Microwave network architectures' },
  { icon: BarChart3, title: 'Decision-Ready Analytics', desc: 'Data platforms that enable resilience and efficiency' },
  { icon: Building2, title: 'Infrastructure Planning', desc: 'Designing systems for long-term resilience' },
  { icon: Lightbulb, title: 'Emerging Market Transformation', desc: 'Digital growth across Africa and the UK' },
];

const fallbackCompanies = [
  { name: 'Nexintell Limited', role: 'Founder & Director', url: 'https://www.nexintell.co' },
  { name: 'Infralytics-GIIS', role: 'Creator', url: 'https://www.infralytics.co' },
  { name: 'Evolve Digital Africa', role: 'Founder & Lead Engineer', url: 'https://www.evolvedigitalafrica.com.ng' },
  { name: 'Grit & Style', role: 'Co-Founder', url: 'https://www.grit.ng' },
  { name: 'iEvolve CIC', role: 'Founder', url: 'https://www.ievolve.uk' },
];

const fallbackAwards = [
  {
    title: 'Award of Excellence & Innovation',
    description: 'Honoured at the Transformer League of Professionals (TLoP), TTC for outstanding contributions to technology leadership and innovation.',
    presenter: 'Coach Sam Oye',
    date: 'July 2023',
    image: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/4obcjli1_AWARDS.jpg',
  },
  {
    title: 'Digital Technology Ecosystem Award',
    description: 'Recognised for outstanding contribution to the digital technology ecosystem in Abuja by TRECCENTRE.',
    presenter: 'Manasseh Dogon',
    date: 'December 2017',
    image: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/2ineexuw_Award%20Recognition.jpg',
  },
];

const bannerImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/4n69g5hl_banner%201.jpg',
    alt: 'Ogaba Joseph Attah speaking engagement'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/fr3txgja_ME%20AND%20AUDIENCE.jpg',
    alt: 'Ogaba Joseph Attah with audience'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/nfe9qexr_MY%20AUDIENCE%201.jpg',
    alt: 'Audience at speaking event'
  }
];

const pressOutlets = [
  { name: 'Global TV', tag: 'Pan-African Broadcast' },
  { name: 'The Guardian', tag: 'Nigeria' },
  { name: 'BusinessDay', tag: 'Feature' },
  { name: 'Vanguard', tag: 'Opinion' },
  { name: 'The Nation', tag: 'Analysis' },
  { name: 'Leadership', tag: 'Commentary' },
  { name: 'Techpoint Africa', tag: 'Tech Press' },
];

export default function HomePage() {
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [companies, setCompanies] = useState(fallbackCompanies);
  const [awards, setAwards] = useState(fallbackAwards);

  useEffect(() => {
    fetchContent('companies').then((items) => {
      if (items && items.length) setCompanies(items);
    });
    fetchContent('awards').then((items) => {
      if (items && items.length) setAwards(items);
    });
  }, []);

  // Auto-scroll banner
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + bannerImages.length) % bannerImages.length);
  };

  return (
    <>
      <SEOHead
        title="Ogaba Joseph Attah | Technology Founder & Digital Infrastructure Intelligence Leader"
        description="Ogaba Joseph Attah is a technology entrepreneur and founder of Nexintell Limited, Infralytics-GIIS, and Evolve Digital Africa, specialising in digital infrastructure intelligence and scalable connectivity systems."
        canonicalUrl="https://www.ogabajosephattah.com"
        jsonLd={jsonLdSchema}
      />
      
      <Navigation />
      
      <main>
        {/* Hero Banner Section */}
        <section className="relative min-h-[90vh] flex items-center pt-24 lg:pt-0" data-testid="hero-section">
          {/* Background Banner Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/fug004q8_banner%201.jpg"
              alt="Ogaba Joseph Attah speaking to audience"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  Technology Founder
                </p>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="hero-title">
                  Ogaba Joseph Attah
                </h1>
                <h2 className="text-lg md:text-xl text-muted-foreground font-medium mb-8">
                  Technology Founder & Digital Infrastructure Intelligence Leader
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-10 max-w-xl">
                  A technology entrepreneur and systems leader working at the intersection of digital infrastructure, data intelligence, and decision-making. Designing scalable platforms and infrastructure architectures that help governments, enterprises, and emerging markets plan, deploy, and govern digital systems more effectively.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" className="rounded-md" data-testid="hero-cta-biography">
                    <Link to="/about-ogaba-joseph-attah">
                      View Biography
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="rounded-md" data-testid="hero-cta-companies">
                    <Link to="/companies">
                      Companies & Ventures
                    </Link>
                  </Button>
                </div>
              </motion.div>

              {/* Hero Image - Speaking */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative hidden lg:block"
              >
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-muted shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/4z6yus2r_IMG-20241020-WA0050.jpg"
                    alt="Ogaba Joseph Attah - Technology Founder"
                    className="w-full h-full object-cover"
                    data-testid="hero-image"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary/10 rounded-2xl -z-10" />
                <div className="absolute -top-4 -left-4 w-16 h-16 border-2 border-primary/20 rounded-xl -z-10" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Founder Snapshot */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="founder-snapshot">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-16"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                Leadership
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Founder Snapshot
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {companies.map((company, index) => (
                <motion.a
                  key={company.name}
                  href={company.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group p-6 bg-card border border-border rounded-xl hover:-translate-y-1 hover:shadow-lg hover:border-primary/30 transition-all duration-300"
                  data-testid={`company-card-${index}`}
                >
                  <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                    {company.role}
                  </p>
                  <h3 className="text-lg font-semibold mb-3 group-hover:text-primary transition-colors flex items-center gap-2">
                    {company.name}
                    <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* As Seen On - Press Strip */}
        <section className="py-12 md:py-16 border-y border-border bg-background" data-testid="as-seen-on">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <Link
              to="/media"
              className="block group"
              data-testid="as-seen-on-link"
            >
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
              >
                <div className="flex-shrink-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-primary mb-1">
                    As Seen On
                  </p>
                  <p className="text-xs text-muted-foreground group-hover:text-primary transition-colors inline-flex items-center gap-1">
                    View all coverage <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </p>
                </div>
                <div className="flex-1 flex flex-wrap items-center gap-x-8 gap-y-4 md:gap-x-10">
                  {pressOutlets.map((outlet, index) => (
                    <motion.div
                      key={outlet.name}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="flex flex-col"
                      data-testid={`press-outlet-${index}`}
                    >
                      <span className="text-base md:text-lg font-serif font-semibold tracking-tight text-foreground/80 group-hover:text-foreground transition-colors leading-none">
                        {outlet.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                        {outlet.tag}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Link>
          </div>
        </section>

        {/* Adoptic Live Product Preview */}
        <section className="py-20 md:py-28 relative overflow-hidden" data-testid="adoptic-preview">
          {/* Decorative grid background */}
          <div
            className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              {/* Copy */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-5"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  Live · Stockton-on-Tees
                </div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">
                  Nexintell Flagship · Adoptic
                </p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] mb-5" data-testid="adoptic-preview-title">
                  See the Digital Adoption Map in action.
                </h2>
                <p className="text-muted-foreground text-base md:text-lg mb-8 max-w-lg">
                  Adoptic is currently deployed as the <span className="text-foreground font-medium">Stockton Digital Register</span> — measuring business digital adoption, connectivity infrastructure, and deprivation at the postcode level, in real time, for every business in the geography.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-sm">
                    <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><span className="text-foreground font-medium">Postcode-level</span> visibility for every business</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <Activity className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Tracks <span className="text-foreground font-medium">connectivity investment → economic activity</span></span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <Radio className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Real-time, continuously rerunnable — no consultancy waits</span>
                  </li>
                </ul>

                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://adoptic.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
                    data-testid="adoptic-explore-live"
                  >
                    Explore the Live Product
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Link
                    to="/companies"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-semibold hover:border-primary/40 hover:text-primary transition-colors"
                    data-testid="adoptic-learn-more"
                  >
                    Learn about Adoptic
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>

              {/* Live embed preview */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="lg:col-span-7 relative"
              >
                <a
                  href="https://adoptic.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-2xl overflow-hidden border border-border bg-card shadow-2xl hover:shadow-primary/10 hover:border-primary/30 transition-all duration-500"
                  data-testid="adoptic-preview-link"
                  aria-label="Open Stockton Digital Register in a new tab"
                >
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-muted/60 border-b border-border">
                    <div className="flex gap-1.5">
                      <span className="h-3 w-3 rounded-full bg-red-400/70" />
                      <span className="h-3 w-3 rounded-full bg-yellow-400/70" />
                      <span className="h-3 w-3 rounded-full bg-green-400/70" />
                    </div>
                    <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-background/80 border border-border/60 flex items-center gap-2 text-xs text-muted-foreground truncate">
                      <Globe className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">adoptic.io</span>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>

                  {/* Iframe live preview */}
                  <div className="relative aspect-[16/10] bg-muted overflow-hidden">
                    <iframe
                      src="https://adoptic.io/"
                      title="Stockton Digital Register · Live Preview"
                      loading="lazy"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      sandbox="allow-scripts allow-same-origin"
                    />
                    {/* Click-through overlay */}
                    <div className="absolute inset-0 bg-transparent group-hover:bg-primary/[0.04] transition-colors" />
                    {/* Hover CTA badge */}
                    <div className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      Open Stockton Digital Register
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </a>

                {/* Decorative accent */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary/10 rounded-2xl -z-10 hidden lg:block" />
                <div className="absolute -top-4 -left-4 w-16 h-16 border-2 border-primary/20 rounded-xl -z-10 hidden lg:block" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Key Focus Areas */}
        <section className="py-24 md:py-32" data-testid="focus-areas">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-16"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                Expertise
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Key Focus Areas
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {focusAreas.map((area, index) => (
                <motion.div
                  key={area.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors"
                  data-testid={`focus-area-${index}`}
                >
                  <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                    <area.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{area.title}</h3>
                  <p className="text-sm text-muted-foreground">{area.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Recognition & Awards */}
        <section className="py-24 md:py-32" data-testid="awards-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-16 text-center"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                Recognition
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Awards & Achievements
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {awards.map((award, index) => (
                <motion.div
                  key={award.id || award.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="group"
                  data-testid={`award-card-${index}`}
                >
                  <div className="relative aspect-square rounded-2xl overflow-hidden bg-muted shadow-xl mb-6">
                    <img
                      src={award.image}
                      alt={award.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      data-testid={`award-image-${index}`}
                    />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold">{award.title}</h3>
                    <p className="text-muted-foreground">{award.description}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      {award.presenter && (
                        <span className="text-primary font-medium">Presented by {award.presenter}</span>
                      )}
                      {award.presenter && award.date && <span className="text-muted-foreground">•</span>}
                      {award.date && <span className="text-muted-foreground">{award.date}</span>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section - Combined with Ready to Connect */}
        <section className="py-24 md:py-32 relative overflow-hidden" data-testid="newsletter-section">
          {/* Background Image - Full opacity */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/4n69g5hl_banner%201.jpg"
              alt="Speaking engagement background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/80" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                  Stay Informed
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
                  Ready to Connect?
                </h2>
                <p className="text-muted-foreground mb-6">
                  For speaking engagements, advisory work, media enquiries, or collaboration opportunities. Receive periodic insights on digital infrastructure intelligence, emerging market technology, and systems thinking.
                </p>
                <Button asChild size="lg" className="rounded-md mb-6" data-testid="cta-contact-btn">
                  <Link to="/contact">
                    Get in Touch
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <NewsletterForm />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Scrolling Banner Section */}
        <section className="relative h-[500px] md:h-[600px] overflow-hidden" data-testid="banner-carousel">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentBannerIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0"
            >
              <img
                src={bannerImages[currentBannerIndex].src}
                alt={bannerImages[currentBannerIndex].alt}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
          
          {/* Navigation Arrows */}
          <button
            onClick={prevBanner}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors z-10"
            data-testid="banner-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={nextBanner}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors z-10"
            data-testid="banner-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {bannerImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBannerIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentBannerIndex 
                    ? 'bg-white w-8' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                data-testid={`banner-dot-${index}`}
              />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
